import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Receipt as ReceiptIcon, Download, ExternalLink, Search } from 'lucide-react';
import { format } from 'date-fns';
import { downloadReceiptPdf, openReceiptPdf, type ReceiptRecord } from '@/lib/receiptPdf';
import { toast } from 'sonner';

export default function MyReceipts() {
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-receipts', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipts' as any)
        .select('*')
        .eq('user_id', user!.id)
        .order('issued_at', { ascending: false });
      if (error) throw error;
      return (data as unknown as ReceiptRecord[]) || [];
    },
  });

  const filtered = (data || []).filter((r) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    const itemDescs = Array.isArray(r.items)
      ? (r.items as any[]).map((i) => i.description).join(' ')
      : '';
    return (
      r.receipt_number.toLowerCase().includes(needle) ||
      (r.payment_reference || '').toLowerCase().includes(needle) ||
      itemDescs.toLowerCase().includes(needle)
    );
  });

  const handle = async (r: ReceiptRecord, action: 'download' | 'view') => {
    try {
      setBusyId(r.id);
      if (action === 'download') await downloadReceiptPdf(r);
      else await openReceiptPdf(r);
    } catch (e: any) {
      toast.error('Could not generate PDF: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="flex items-center gap-2">
          <ReceiptIcon className="h-5 w-5 text-primary" /> My Receipts
        </CardTitle>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search receipt no., reference, item…"
            className="pl-9"
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40" />
        ) : !filtered.length ? (
          <p className="text-sm text-muted-foreground py-10 text-center">
            No receipts yet. They appear here automatically after a successful purchase.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt No.</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Item(s)</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Total (incl. VAT)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const items = Array.isArray(r.items) ? (r.items as any[]) : [];
                  const summary = items
                    .map((i) => `${i.description}${i.qty > 1 ? ` × ${i.qty}` : ''}`)
                    .join(', ');
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                      <TableCell className="text-xs">
                        {format(new Date(r.issued_at), 'd MMM yyyy, HH:mm')}
                      </TableCell>
                      <TableCell className="text-sm max-w-[260px] truncate" title={summary}>
                        {summary || '—'}
                      </TableCell>
                      <TableCell className="text-xs capitalize">
                        {(r.payment_method || '—').replace('_', ' ')}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {Number(r.total_gross).toLocaleString('en-US', { minimumFractionDigits: 2 })}{' '}
                        <span className="text-xs text-muted-foreground">{r.currency}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={r.status === 'issued' ? 'default' : 'secondary'}>
                          {r.status === 'issued' ? 'Paid' : r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === r.id}
                            onClick={() => handle(r, 'view')}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            disabled={busyId === r.id}
                            onClick={() => handle(r, 'download')}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" /> PDF
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
